import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { formatIST } from "@/lib/utils";
import { Users, Plus, Edit2, Trash2 } from "lucide-react";

/**
 * Who is on this order: whoever booked it, and whoever was tagged onto it since.
 *
 * It was three cards deep — a panel holding a gradient-filled card with a 4px
 * blue edge and a 40px avatar, then one bordered card per person — so a sidebar
 * whose whole content is four short facts shouted louder than the order it sits
 * beside. Every person is the same quiet row now: name, role, when. The creator
 * is not styled differently from the rest; the label above the row is what says
 * they are the creator, which is the fact, and it reads in one pass.
 *
 * @param {Object} order - created_by, created_at, creator_details, stakeholder_tags
 * @param {Function} onAddStakeholder - open the add dialog
 * @param {Function} onEditStakeholder - edit a tag (receives the tag)
 * @param {Function} onRemoveStakeholder - remove a tag (receives the tag id)
 * @param {Object} currentUser - the signed-in user
 */

// mirrors STAKEHOLDER_TAG_ROLES in backend/models/core.py
const TAGGING_ROLES = [
  "admin", "receptionist", "designer", "accountant",  // "head_designer" — post open
  "supervisor", "city_office_manager",
];

const rolesOf = (user) => (user?.roles?.length ? user.roles : [user?.role]).filter(Boolean);

const readableRole = (role) => (role ? role.replace(/_/g, " ") : "");

function GroupLabel({ children }) {
  return (
    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-400">{children}</p>
  );
}

function PersonRow({ name, role, meta, note, actions }) {
  return (
    <div className="flex items-start gap-2 py-1.5">
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-baseline gap-x-1.5 leading-tight">
          <span className="truncate text-[13px] font-medium text-slate-800">{name}</span>
          {role && <span className="text-[11px] text-slate-400">{readableRole(role)}</span>}
        </p>
        {note && <p className="mt-0.5 text-[11px] leading-snug text-slate-600">“{note}”</p>}
        {meta && <p className="mt-0.5 text-[11px] text-slate-400">{meta}</p>}
      </div>
      {actions}
    </div>
  );
}

const InvolvedPeopleSection = ({
  order,
  onAddStakeholder,
  onEditStakeholder,
  onRemoveStakeholder,
  currentUser,
}) => {
  const myRoles = rolesOf(currentUser);
  const canTagStakeholders = myRoles.some((r) => TAGGING_ROLES.includes(r));
  const isAdmin = myRoles.includes("admin");

  const canEditTag = (tag) => {
    if (!currentUser) return false;
    return isAdmin || tag.tagged_by === currentUser.id;
  };

  const sortedTags = order.stakeholder_tags
    ? [...order.stakeholder_tags].sort(
        (a, b) => new Date(a.tagged_at) - new Date(b.tagged_at)
      )
    : [];

  return (
    <Card className="border-slate-100 shadow-card rounded-xl">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 font-heading text-sm font-semibold uppercase tracking-wider text-slate-600">
          <Users className="h-3.5 w-3.5" /> Involved People
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <GroupLabel>Created by</GroupLabel>
          <PersonRow
            name={order.creator_details?.full_name || order.created_by_name || "Unknown"}
            role={order.creator_details?.role}
            meta={formatIST(order.created_at, { compact: true })}
          />
        </div>

        {sortedTags.length > 0 ? (
          <div>
            <GroupLabel>Also involved</GroupLabel>
            <div className="divide-y divide-slate-100">
              {sortedTags.map((tag) => (
                <PersonRow
                  key={tag.id}
                  name={tag.user_name}
                  role={tag.user_role}
                  note={tag.role_note}
                  meta={`Tagged by ${tag.tagged_by_name || "Unknown"} · ${formatIST(tag.tagged_at, { compact: true })}`}
                  actions={canEditTag(tag) && (
                    <span className="flex shrink-0 items-center">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-300 hover:bg-transparent hover:text-brand-blue"
                        onClick={() => onEditStakeholder(tag)}
                        title="Edit role note"
                      >
                        <Edit2 size={12} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-slate-300 hover:bg-transparent hover:text-red-600"
                        onClick={() => onRemoveStakeholder(tag.id)}
                        title="Remove person"
                      >
                        <Trash2 size={12} />
                      </Button>
                    </span>
                  )}
                />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-[11px] text-slate-400">No one else tagged yet</p>
        )}

        {canTagStakeholders && onAddStakeholder && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddStakeholder}
            className="h-7 w-full justify-start gap-1.5 px-1 text-xs font-normal text-slate-500 hover:bg-slate-50 hover:text-brand-blue"
          >
            <Plus className="h-3.5 w-3.5" /> Add person
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default InvolvedPeopleSection;
